import { QuestionType } from "api/landlord-surveys/types/questions";
import { DraftSurveyTemplateQuestion } from "../types";
import { useSurveyStore, selectSortedQuestions } from "../store";
import { cx } from "shared/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  TextFontIcon,
  ToggleOnIcon,
  CheckListIcon,
  RadioButtonIcon,
  Calendar03Icon,
  Attachment01Icon,
  Location01Icon,
  DragDropVerticalIcon,
  PlusSignCircleIcon,
  PlusSignIcon
} from "@hugeicons-pro/core-stroke-rounded";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "@hello-pangea/dnd";

const iconMap: Record<QuestionType, typeof TextFontIcon> = {
  [QuestionType.LONG_TEXT]: TextFontIcon,
  [QuestionType.YES_NO]: ToggleOnIcon,
  [QuestionType.CHECKBOX_MULTI]: CheckListIcon,
  [QuestionType.RADIO_GROUP]: RadioButtonIcon,
  [QuestionType.DATE]: Calendar03Icon,
  [QuestionType.FILE_SINGLE]: Attachment01Icon,
  [QuestionType.ADDRESS]: Location01Icon
};

const IconMap = ({ type }: { type: QuestionType }) => (
  <HugeiconsIcon
    icon={iconMap[type]}
    size={20}
    strokeWidth={1.5}
    className="text-content-1000"
  />
);
import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps
} from "@hello-pangea/dnd";
import { forwardRef } from "react";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";

type DraggableProvidedProps = {
  draggableProps: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
};

const QuestionCard = forwardRef<
  HTMLDivElement,
  DraftSurveyTemplateQuestion &
    DraggableProvidedProps & { addNewQuestion: (afterIndex?: number) => void }
>(
  (
    { dragHandleProps, draggableProps, isDragging, addNewQuestion, ...props },
    ref
  ) => {
    const activeQuestionIdx = useSurveyStore(
      (state) => state.activeQuestionIdx
    );
    const updateActiveQuestionIdx = useSurveyStore(
      (state) => state.updateActiveQuestionIdx
    );
    const { style, ...restDraggableProps } = draggableProps;
    return (
      <div
        ref={ref}
        className="border relative border-alpha-100 bg-white rounded-md shadow-sm group h-[52px] px-4 py-2 flex items-center gap-2 select-none"
        style={{
          ...style,
          transform: isDragging
            ? `${style?.transform ?? ""} rotate(5deg)`
            : style?.transform
        }}
        onClick={() => updateActiveQuestionIdx(props.index)}
        {...restDraggableProps}
      >
        <div className="px-3 py-1.5 border border-alpha-100 rounded-md flex gap-2 shrink-0">
          <span className="text-sm text-content-1000 font-medium">
            {props.index + 1}
          </span>
          <IconMap type={props.type} />
        </div>
        <p
          className={cx(
            "truncate text-sm transition-all",
            props.index === activeQuestionIdx
              ? "text-content-1000 font-bold"
              : "text-content-300 font-medium"
          )}
        >
          {props.question}
        </p>
        <div
          className="ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
          {...dragHandleProps}
        >
          <HugeiconsIcon
            icon={DragDropVerticalIcon}
            size={24}
            strokeWidth={1.5}
            className="text-content-1000"
          />
        </div>

        <div
          role="button"
          aria-description={`Add question block after question #${props.index + 1}`}
          className="absolute group-hover:block hidden -bottom-2.5 left-1/2 -translate-x-1/2 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            addNewQuestion(props.index + 1);
          }}
        >
          <HugeiconsIcon
            icon={PlusSignCircleIcon}
            size={20}
            className="text-content-200 bg-white rounded-full"
          />
        </div>
      </div>
    );
  }
);

QuestionCard.displayName = "QuestionCard";

export const SidebarQuestions = () => {
  const data = useSurveyStore((state) => state.data);

  const updateData = useSurveyStore((state) => state.updateData);

  const updateQuestionOrderByIdx = useSurveyStore(
    (state) => state.updateQuestionOrderByIdx
  );

  const addNewQuestion = useSurveyStore((state) => state.addNewQuestion);
  const questions = useSurveyStore(selectSortedQuestions);
  if (!data) return null;

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    updateQuestionOrderByIdx(result.source.index, result.destination.index);
  };

  return (
    <div className="max-w-[300px] flex flex-col h-[calc(100vh-72px)] w-full border-r border-alpha-80 bg-white">
      <div className="pt-6 px-4 shrink-0">
        <div className="pb-6 border-b border-alpha-40">
          <BaseInput
            id="title"
            maxLength={30}
            value={data.name || ""}
            onChange={(v) => {
              updateData({ ...data, name: v }, ["name"]);
            }}
            hideOptional
            label="Survey name"
          />
        </div>
      </div>
      <div className="flex-grow overflow-y-auto px-4 py-4">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="list">
            {(provided) => (
              <ul
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-col gap-4"
              >
                {questions.map((question, i) => (
                  <Draggable
                    key={question.internal_id}
                    draggableId={question.internal_id}
                    index={i}
                  >
                    {(provided, snapshot) => (
                      <QuestionCard
                        ref={provided.innerRef}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        isDragging={snapshot.isDragging}
                        addNewQuestion={addNewQuestion}
                        {...question}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <div className="px-6 py-4 border-t border-alpha-100">
        <Button
          className="w-full"
          HugeIcon={PlusSignIcon}
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          onClick={() => addNewQuestion()}
        >
          Add question
        </Button>
      </div>
    </div>
  );
};
